import React, { useEffect } from 'react';
import { AppContext } from '@thd-nucleus/app-render';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Metadata } from '@thd-olt-component-react/metadata';
import { Typography } from '@one-thd/sui-atomic-components';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { useNavigate } from '@thd-olt-component-react/router';
import { HybridContentfulWidgets, useHybridPLP } from '@thd-olt-component-react/hybrid';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';

import { Col, Row } from '@thd-olt-component-react/core-ui';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import proptypes from 'prop-types';
const { string } = proptypes;
import { extend } from '@thd-nucleus/data-sources';
import classNames from 'classnames/bind.js';
import {
  imagePreloader,
  getAnalyticsPageType, getContentBreadCrumbLabels, renderRobotTag
} from '../../utils/utils';
import styles from '../../templates/giftCard-layout.scss';
import { BRAND_PAGE } from '../../../utils/consts';

export const HybridLayout = ({
  pageId, customerType, pathWithQueryParams, previewLink
}) => {
  const cx = classNames.bind(styles);
  const { instance } = React.useContext(AppContext);
  const navigate = useNavigate();

  const hybridParams = {
    slug: pageId,
    customerType,
    instance
  };

  const path = `/c/${pageId}`;

  const { data, contentElements, loading, error } = useHybridPLP(hybridParams);

  if (error) {
    navigate('/error');
  }

  useHelmet('general-merchandise', { data }, imagePreloader, [data]);
  const layouts = data?.layouts;
  const crumbs = layouts?.breadcrumbs?.breadcrumbItem || [];
  const metadata = layouts?.seo || {};
  const isBrandPage = layouts?.content?.pageType === BRAND_PAGE;
  const { linkData, scriptData, ...metadataMinusLinkDataAndScriptData } = metadata;

  const breadCrumbLabels = getContentBreadCrumbLabels(crumbs);
  const pageType = getAnalyticsPageType(pathWithQueryParams);
  const breadcrumbs = {
    searchModel: {
      taxonomy: {
        breadCrumbs: crumbs
      }
    }
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('speciality-content.page-load', {
      breadCrumbLabels,
      pageType,
      isBrandPage
    });
  }, [data]);

  return (
    <Row>
      <ErrorBoundary name="metadata">
        <Metadata data={{ ...metadataMinusLinkDataAndScriptData }}>
          {renderRobotTag(metadata, previewLink)}
        </Metadata>
      </ErrorBoundary>
      <Row className="isBound">
        <Col xs="12" className={cx('layout-top__no-bottom-padding')}>
          <div>
            {breadcrumbs && (
              <ErrorBoundary name="breadcrumbs">
                <Breadcrumbs staticCrumbs={crumbs} data={breadcrumbs} />
              </ErrorBoundary>
            )}
          </div>
        </Col>
        <Col xs="12">
          <div>
            <ErrorBoundary name="title">
              <Typography variant="h1" weight="display" height="snug" color="primary" uppercase>
                {data?.layouts?.title}
              </Typography>
            </ErrorBoundary>
          </div>
          <div>
            {!loading
              && (
                <ErrorBoundary name="contentful-widgets">
                  <HybridContentfulWidgets
                    contentElements={contentElements}
                    loading={loading}
                  />
                </ErrorBoundary>
              )}
          </div>
          {!loading && (
            <div className="sui-mt-4">
              <EmtLinks
                contentId={pageId}
                pageName="articles"
                path={path}
              />
            </div>
          )}
        </Col>
      </Row>
    </Row>
  );
};

HybridLayout.propTypes = {
  pageId: string.isRequired,
  customerType: string.isRequired,
  pathWithQueryParams: string.isRequired,
  previewLink: string.isRequired
};

HybridLayout.displayName = 'HybridLayout';

HybridLayout.dataModel = extend(
  HybridContentfulWidgets.dataModel,
  EmtLinks
);
